<template>
  <v-dialog
    :value="isDialogOpen"
    @input="closeDialog"
    :max-width="modalSettings.width"
    :content-class="modalSettings.contentClass"
  >
    <component
      :is="currentComponent"
      :loading="loading"
      :contact="contact"
      :contactId="contactId"
      :serverError="serverError"
      :rawInterviews="interviews"
      :zipTimezone="zipTimezone"
      @confirm="onContactConfirmed"
      @sendMessage="sendMessage"
      @nextContact="moveToNextContact"
      @close="closeDialog"
    />
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';

import { sendSMS } from '@/views/recruiting/recruitingHelpers';
import EventBus from '@/factories/eventBus';

import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';

import VectorOnDemandInfo from '@/views/recruiting/dialogs/interview/VectorOnDemandInfo.vue';
import VectorSelectInterview from '@/views/recruiting/dialogs/interview/VectorSelectInterview.vue';
import VectorInterviewError from '@/views/recruiting/dialogs/interview/VectorInterviewError.vue';
import ContactInfoConfirmation from '@/views/recruiting/dialogs/interview/ContactInfoConfirmation.vue';

export default {
  name: 'RecruitingAppointmentDialog',
  components: {
    VectorOnDemandInfo,
    VectorSelectInterview,
    VectorInterviewError,
    ContactInfoConfirmation,
  },
  data() {
    return {
      step: 'confirm',
      serverError: null,
      interviews: [],
      zipTimezone: '',
      contactId: null,
      isDialogOpen: false,

      contactLoading: false,
      interviewsLoading: false,
    };
  },
  computed: {
    ...mapGetters('users', ['currentUser']),
    ...mapGetters(['singleContact']),
    loading() {
      return this.contactLoading || this.interviewsLoading;
    },
    contact() {
      return this.singleContact(this.contactId);
    },
    hasError() {
      if (this.serverError) return true;

      if (!this.interviews.length && !this.isOnDemand) return true;

      const { recruit_id, training_set } = this.contact || {};
      return !recruit_id || training_set;
    },
    isOnDemand() {
      return (this.contact?.tags || []).includes('odi');
    },
    currentComponent() {
      if (this.step === 'confirm') return 'ContactInfoConfirmation';
      if (this.hasError) return 'VectorInterviewError';
      if (this.isOnDemand) return 'VectorOnDemandInfo';
      return 'VectorSelectInterview';
    },
    modalSettings() {
      const isSmall =
        this.step === 'confirm' || this.isOnDemand || this.hasError;
      return {
        width: isSmall ? '480px' : '980px',
        contentClass: isSmall ? 'fixed-height-md' : 'fixed-height-lg',
      };
    },
  },
  watch: {
    currentComponent: {
      immediate: true,
      handler(val) {
        this.$emit('changeModalComponent', val);
      },
    },
  },
  methods: {
    ...mapMutations(['updateContactInList']),

    ...mapActions([
      'loadSingleContactShort',
      'updateContact',
      'updateContactField',
      'sendContactEvent',
    ]),

    ...mapActions('vector', [
      'loadRecruitInterviews',
      'getOfficeData',
      'ensureRecruit',
      'loadVectorSnippets',
    ]),

    sendMessage({ message, type }) {
      sendSMS([this.contact.mobile], message);
      this.recordEvent(type);
    },

    moveToNextContact() {
      this.updateContactLastContacted();
      this.closeDialog();
    },

    async recordEvent(type) {
      const event_type =
        type === 'offer' ? 'received_sms' : 'interview_scheduled';

      return this.sendContactEvent({
        contactId: this.contactId,
        params: {
          event_type,
          properties: {
            timestamp: Math.floor(Date.now() / 1000),
            contactId: this.contactId,
            userId: this.currentUser.id,
          },
        },
      });
    },

    async loadInterviewInfo() {
      const { training_set, recruit_id } = this.contact || {};

      if (!recruit_id || training_set) return;

      try {
        const res = await this.loadRecruitInterviews(this.contact);

        const { interviews, zipTimezone, tags, errors } = res || {};

        this.interviews = interviews || [];
        this.zipTimezone = zipTimezone;

        await this.handleInterviewInfoErrors(errors);

        this.updateContactOfficeNumber(res);
        this.updateContactTags(tags);
      } catch (e) {
        this.serverError = {
          type: 'info',
          message:
            'Failed to load interview information. Please try again later.',
        };
      }
    },

    async handleInterviewInfoErrors(errors) {
      if (errors) {
        const keys = Object.keys(errors);
        this.serverError = {
          type: 'error',
          message: errors[keys[0]][0],
        };
      }

      if (!this.interviews.length && !this.isOnDemand) {
        this.serverError = {
          type: 'info',
          message: 'No interviews found for the assigned office.',
        };
      }
    },

    extractOfficeNumber(res) {
      const { officeNumber, interviews } = res;
      return officeNumber || (interviews.length ? interviews[0].oleanId : '');
    },

    async updateContactLastContacted() {
      this.updateContactField({
        contactId: this.contactId,
        field: 'last_contacted',
        value: new Date(),
      });
    },

    updateContactOfficeNumber(res) {
      const currentOfficeNumber = this.contact.office_number;
      const officeNumber = this.extractOfficeNumber(res);

      if (!officeNumber || officeNumber === currentOfficeNumber) return;

      this.updateContactField({
        contactId: this.contactId,
        field: 'office_number',
        value: officeNumber,
      });
    },

    updateContactTags(tags) {
      if (!tags) return;

      const { add = [], remove = [] } = tags;
      const currentTags = this.contact?.tags ?? [];

      const updatedTags = [
        ...currentTags.filter((tag) => !remove.includes(tag)),
        ...add.filter((tag) => !currentTags.includes(tag)),
      ];

      if (isEqual(currentTags, updatedTags)) return;

      this.updateContactField({
        contactId: this.contactId,
        field: 'tags',
        value: tags,
        listValue: updatedTags,
      });
    },

    async onContactConfirmed({ zip_code, contactParams } = {}) {
      this.interviewsLoading = true;

      try {
        await this.ensureRecruitId(zip_code);
        await this.saveContactParams(contactParams);
        await this.loadInterviewInfo();
        this.step = 'result';
      } catch (e) {
        this.serverError = {
          type: 'info',
          message:
            'Failed to process contact information. Please try again later.',
        };
        this.step = 'result';
      } finally {
        this.interviewsLoading = false;
      }
    },

    async saveContactParams(params) {
      if (!params || !Object.keys(params).length) return;
      await this.updateContact({
        contactId: this.contactId,
        params,
        skipRefetch: true,
      });
    },

    closeDialog() {
      this.step = 'confirm';
      this.serverError = null;

      this.interviews = [];
      this.zipTimezone = '';
      this.interviewsLoading = false;

      this.contactId = null;
      this.contactLoading = false;

      this.isDialogOpen = false;
    },

    async fetchContact() {
      try {
        this.contactLoading = true;
        if (this.contact) return;

        await this.loadSingleContactShort({
          filters: { id: this.contactId },
          update: true,
        });
      } finally {
        this.contactLoading = false;
      }
    },

    async ensureRecruitId(zip_code) {
      const { recruit_id } = this.contact || {};
      if (!this.contactId || !zip_code || recruit_id) return;

      const res = await this.ensureRecruit({
        contactId: this.contactId,
        zip_code,
      });

      const recruitId = res?.recruit_id;
      if (!recruitId) return;

      this.updateContactInList({
        ...cloneDeep(this.contact),
        recruit_id: recruitId,
      });
    },

    async openDialog({ contactId }) {
      this.step = 'confirm';
      this.contactId = contactId;
      this.fetchContact();
      this.isDialogOpen = true;
    },
  },
  mounted() {
    EventBus.$on('showAppointmentDialog', this.openDialog);
    this.loadVectorSnippets();
  },
  beforeDestroy() {
    EventBus.$off('showAppointmentDialog', this.openDialog);
    this.closeDialog();
  },
};
</script>
